import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Grid, IconButton, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, EmptyState, useConfirm } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { extractErrorMessage } from "../../utils/errorMessage";

const ACCENTS = ["#6C50BF", "#39C645", "#F9BC06", "#FF6B6B", "#5C85FF", "#EE82EE"];

export default function HolidaysListNew() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const nav = useNavigate();
  const { hasRole } = useAuth();
  const confirm = useConfirm();
  const canManage = hasRole("admin", "hr");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/holidays");
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setHolidays([...list].sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { upcoming, past, total, next } = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const up = holidays.filter((h) => new Date(h.date) >= now);
    const pst = holidays.filter((h) => new Date(h.date) < now);
    return { upcoming: up, past: pst, total: holidays.length, next: up[0] || null };
  }, [holidays]);

  const nextDays = next ? Math.max(0, Math.ceil((new Date(next.date) - new Date()) / 86400000)) : null;
  const displayed = tab === 0 ? upcoming : tab === 1 ? past : holidays;

  const handleDelete = async (id) => {
    const ok = await confirm({ title: "Delete holiday?", description: "This cannot be undone.", confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/api/holidays/${id}`);
      setHolidays((arr) => arr.filter((h) => h._id !== id));
      toast.success("Holiday removed");
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Holiday Calendar"
        subtitle={`${new Date().getFullYear()} • ${total} company holidays`}
        icon={<CalendarMonthOutlinedIcon />}
        actions={canManage ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => nav("/HolidayForm")}>
            Add Holiday
          </Button>,
        ] : undefined}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Total Holidays"
            value={total}
            hint="This year"
            icon={<EventAvailableOutlinedIcon />}
            accent="#6C50BF"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Upcoming"
            value={upcoming.length}
            hint={next ? `Next: ${next.name}` : "No upcoming"}
            icon={<AccessTimeOutlinedIcon />}
            accent="#39C645"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Days to next"
            value={nextDays !== null ? (nextDays === 0 ? "Today" : nextDays) : "—"}
            hint={next ? new Date(next.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "Nothing scheduled"}
            icon={<CalendarMonthOutlinedIcon />}
            accent="#F9BC06"
          />
        </Grid>
      </Grid>

      <SectionCard>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Upcoming (${upcoming.length})`} />
          <Tab label={`Past (${past.length})`} />
          <Tab label={`All (${total})`} />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : displayed.length === 0 ? (
          <EmptyState
            icon={<CalendarMonthOutlinedIcon />}
            title="No holidays"
            subtitle={tab === 0 ? "No upcoming holidays scheduled." : tab === 1 ? "No past holidays." : "No holidays have been added yet."}
            action={canManage ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav("/HolidayForm")}>Add Holiday</Button> : null}
          />
        ) : (
          <Grid container spacing={2}>
            {displayed.map((h, idx) => {
              const accent = ACCENTS[idx % ACCENTS.length];
              const date = new Date(h.date);
              const isPast = date < new Date();
              const daysAway = Math.ceil((date - new Date()) / 86400000);
              const isNear = !isPast && daysAway >= 0 && daysAway <= 7;
              return (
                <Grid item xs={12} sm={6} md={4} key={h._id}>
                  <Box
                    sx={{
                      borderRadius: 3, overflow: "hidden",
                      border: "1px solid", borderColor: isNear ? accent : "divider",
                      bgcolor: "background.paper", opacity: isPast ? 0.65 : 1,
                      transition: "all 0.2s", position: "relative",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
                    }}
                  >
                    <Box sx={{ height: 5, bgcolor: accent }} />
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          sx={{
                            minWidth: 56, textAlign: "center", borderRadius: 2,
                            border: "1px solid", borderColor: "divider", overflow: "hidden", flexShrink: 0,
                          }}
                        >
                          <Box sx={{ bgcolor: accent, color: "#fff", py: 0.5 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                              {date.toLocaleDateString("en-IN", { month: "short" })}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 22, fontWeight: 800, py: 0.5 }}>
                            {date.getDate()}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>
                              {h.name}
                            </Typography>
                            {isNear && <Chip label="Soon" size="small" sx={{ bgcolor: `${accent}20`, color: accent, fontWeight: 700, height: 20, fontSize: 10 }} />}
                            {isPast && <Chip label="Past" size="small" sx={{ bgcolor: "action.hover", color: "text.secondary", fontWeight: 600, height: 20, fontSize: 10 }} />}
                          </Stack>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                            {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                          </Typography>
                          {h.type && (
                            <Chip label={h.type} size="small" variant="outlined" sx={{ mt: 1, height: 20, fontSize: 10, textTransform: "capitalize" }} />
                          )}
                        </Box>
                        {canManage && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(h._id)}
                            sx={{ color: "error.main", flexShrink: 0 }}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </SectionCard>
    </Box>
  );
}
